import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Two display modes for the desktop sidebar:
 *
 *  · `expanded` — default 220px column with icons + labels + section headers.
 *  · `icons` — narrow rail (64px) showing icons only; labels surface as
 *    tooltips on hover. Nav-group parents collapse to their parent icon
 *    and navigate to the first sub-item on click.
 *
 * Mobile (<lg) ignores this mode and continues to use the off-canvas
 * drawer driven by `mobileOpen` in AppShell — `icons` only applies on lg+.
 *
 * Present mode hides the sidebar entirely through AppShell's chromeless
 * branch, not through this mode — so there's no user-toggleable "hidden"
 * state.
 */
export type SidebarMode = "expanded" | "icons";

interface SidebarLayoutValue {
  mode: SidebarMode;
  /** Toggle between `expanded` and `icons`, remembered across visits. */
  toggle: () => void;
}

const SidebarLayoutContext = createContext<SidebarLayoutValue | null>(null);

const STORAGE_KEY = "sidebar:mode";

function readStoredMode(): SidebarMode | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "expanded" || raw === "icons") return raw;
    return null;
  } catch {
    return null;
  }
}

function writeStoredMode(mode: SidebarMode) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

interface SidebarLayoutProviderProps {
  children: ReactNode;
}

export function SidebarLayoutProvider({ children }: SidebarLayoutProviderProps) {
  const [mode, setMode] = useState<SidebarMode>(() => readStoredMode() ?? "expanded");

  const toggle = useCallback(() => {
    setMode((prev) => {
      const next: SidebarMode = prev === "icons" ? "expanded" : "icons";
      writeStoredMode(next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ mode, toggle }), [mode, toggle]);

  return (
    <SidebarLayoutContext.Provider value={value}>
      {children}
    </SidebarLayoutContext.Provider>
  );
}

export function useSidebarLayout(): SidebarLayoutValue {
  const ctx = useContext(SidebarLayoutContext);
  if (!ctx) {
    throw new Error(
      "useSidebarLayout must be used inside <SidebarLayoutProvider> — it is mounted by AppShell, so reading it from a public route or before the shell renders is the usual cause.",
    );
  }
  return ctx;
}
